import type { RecipeIngredient } from './recipe.js';

// T-278 (collettiva): calcolo del consumo di una preparazione = ingredienti
// della ricetta × porzioni pianificate. È la base della riserva (impegnato)
// all'avvio e dello scarico effettivo alla chiusura della scheda di produzione.
//
// `qty` qui è il NETTO ASL per porzione (grammatura del ricettario). Lo scarico
// LORDO (netto + % scarto di mondatura/disosso, da T-275) arriverà quando il
// coefficiente di scarto sarà nello schema (campo suggested_scarico per
// ingrediente); finché manca, riserva/scarico usano il netto.

export interface IngredientConsumption {
  food_id: string;
  qty: number; // quantità totale per la preparazione (netto × porzioni)
  uom: string;
  per_portion: number; // netto per porzione (dalla ricetta)
}

// Espande gli ingredienti di una ricetta per N porzioni. Ignora le righe senza
// food_id o senza qty (es. "q.b."): non si può impegnare/scaricare una quantità
// non quantificata.
export function computeRecipeConsumption(
  ingredients: RecipeIngredient[],
  portions: number,
): IngredientConsumption[] {
  if (!Number.isFinite(portions) || portions <= 0) return [];
  const out: IngredientConsumption[] = [];
  for (const ing of ingredients) {
    if (!ing.food_id) continue;
    if (ing.qty == null || !Number.isFinite(ing.qty) || ing.qty <= 0) continue;
    out.push({
      food_id: ing.food_id,
      per_portion: ing.qty,
      qty: round3(ing.qty * portions),
      uom: ing.uom ?? 'g',
    });
  }
  return out;
}

// Aggrega il consumo di più preparazioni (più righe di piano) per food_id+uom:
// lo stesso alimento usato in più piatti del giorno si impegna/scarica una volta.
export function aggregateConsumption(rows: IngredientConsumption[]): IngredientConsumption[] {
  const byKey = new Map<string, IngredientConsumption>();
  for (const r of rows) {
    const key = `${r.food_id}|${r.uom}`;
    const cur = byKey.get(key);
    if (cur) {
      cur.qty = round3(cur.qty + r.qty);
    } else {
      byKey.set(key, { ...r });
    }
  }
  return [...byKey.values()];
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}
